import { Injectable } from "@angular/core";
import { RoutingHistoryService } from "../routing-history/routing-history.service";
import { GridFiltersKey, PageNumberKey, MainFiltersKey, PreviousProjectTypeKey, GridParamsRestoringResult, SortedColumnKey, EditedItemIdKey, PageSizeKey, InnerParamsKey } from "./grid-params-restoring.model";
import { ProjectType } from "../../../projects/projects.model";
import { GridComponent } from '../../shared/grid/grid.component';

@Injectable()
export class GridParamsRestoringService {
    constructor(
        private routingHistory: RoutingHistoryService
    ) { }

    public saveCurrentGridParams(grid: GridComponent, editedItemId: any = undefined, projectType: ProjectType = undefined, isForInner: boolean = false) {
        sessionStorage.setItem(this.getKeyName(GridFiltersKey, isForInner), JSON.stringify(grid.filters));
        if (grid.mainFilter) {
            sessionStorage.setItem(this.getKeyName(MainFiltersKey, isForInner), JSON.stringify(grid.mainFilter));
        }
        sessionStorage.setItem(this.getKeyName(PageNumberKey, isForInner), grid.currentPage.toString());
        sessionStorage.setItem(this.getKeyName(PageSizeKey, isForInner), grid.pageSize.toString());
        sessionStorage.setItem(this.getKeyName(SortedColumnKey, isForInner), JSON.stringify({ columnName: grid.sortedColumn, isAscending: grid.isAsc }));
        if (editedItemId != undefined) {
            sessionStorage.setItem(this.getKeyName(EditedItemIdKey, isForInner), editedItemId.toString());
        }
        if (projectType != undefined) {
            sessionStorage.setItem(PreviousProjectTypeKey, projectType.toString());
        }
    }

    public saveAddingNewRowInfo(addedItemId: any, projectType: ProjectType = undefined, isForInner: boolean = false) {
        sessionStorage.setItem(this.getKeyName(EditedItemIdKey, isForInner), addedItemId.toString());
        if (projectType !== undefined) {
            sessionStorage.setItem(PreviousProjectTypeKey, projectType.toString());
        }
    }

    public getPreviousGridParams(acceptedPreviousPath: string, isForInner: boolean = false): GridParamsRestoringResult {
        let previousPath = this.routingHistory.getPreviousUrl();
        if (previousPath.startsWith(acceptedPreviousPath) || previousPath.includes(acceptedPreviousPath)) {
            let result = new GridParamsRestoringResult();

            let previousFilters = sessionStorage.getItem(this.getKeyName(GridFiltersKey, isForInner));
            if (previousFilters) {
                result.initialFilters = JSON.parse(previousFilters);
            }

            let previousPageNumber = sessionStorage.getItem(this.getKeyName(PageNumberKey, isForInner));
            if (previousPageNumber) {
                result.initialPageNumber = parseInt(previousPageNumber);
            }

            let previousPageSize = sessionStorage.getItem(this.getKeyName(PageSizeKey, isForInner));
            if (previousPageSize) {
                result.initialPageSize = parseInt(previousPageSize);
            }

            let previousSortingStr = sessionStorage.getItem(this.getKeyName(SortedColumnKey, isForInner));
            if (previousSortingStr) {
                let previousSortedColumn = JSON.parse(previousSortingStr);
                result.sortedColumn = previousSortedColumn;
            }

            let editedItemIdStr = sessionStorage.getItem(this.getKeyName(EditedItemIdKey, isForInner));
            if (editedItemIdStr) {
                let editedItemId = Number(editedItemIdStr);
                result.editedItemId = isNaN(editedItemId) ? editedItemIdStr : editedItemId;
            }

            let previousMainFiltersStr = sessionStorage.getItem(this.getKeyName(MainFiltersKey, isForInner));
            if (previousMainFiltersStr) {
                let previousMainFilters = JSON.parse(previousMainFiltersStr);
                if (previousMainFilters.length > 0) {
                    result.mainFilter = previousMainFilters;
                    previousMainFilters.forEach(filter => {
                        result.previousMainFilters[filter.internalName] = filter.value;
                    });
                }
            }

            if (isForInner == false) {
                this.clearSavedFilterData();
            }
            this.clearSavedFilterData(true);
            return result;
        }

        if (isForInner == false) {
            this.clearSavedFilterData();
        }
        this.clearSavedFilterData(true);

        return undefined;
    }

    public getPreviousProjectsGridParams(acceptedPreviousPaths: string[], projectType: ProjectType): GridParamsRestoringResult {
        let previousPath = this.routingHistory.getPreviousUrl();
        let isPreviousPathFromDetails = false;
        acceptedPreviousPaths.forEach(path => {
            if (previousPath.startsWith(path)) {
                isPreviousPathFromDetails = true;
            }
        });
        if (isPreviousPathFromDetails) {
            let previousProjectTypeStr = sessionStorage.getItem(PreviousProjectTypeKey);
            if (previousProjectTypeStr) {
                var previousProjectType = parseInt(previousProjectTypeStr);
            }

            if (previousProjectType == projectType) {
                let result = new GridParamsRestoringResult();

                let previousFilters = sessionStorage.getItem(GridFiltersKey);
                if (previousFilters) {
                    result.initialFilters = JSON.parse(previousFilters);
                }

                let previousPageNumber = sessionStorage.getItem(PageNumberKey);
                if (previousPageNumber) {
                    result.initialPageNumber = parseInt(previousPageNumber);
                }

                let previousPageSize = sessionStorage.getItem(PageSizeKey);
                if (previousPageSize) {
                    result.initialPageSize = parseInt(previousPageSize);
                }

                let previousSortingStr = sessionStorage.getItem(SortedColumnKey);
                if (previousSortingStr) {
                    let previousSortedColumn = JSON.parse(previousSortingStr);
                    result.sortedColumn = previousSortedColumn;
                }

                let editedItemIdStr = sessionStorage.getItem(EditedItemIdKey);
                if (editedItemIdStr) {
                    let editedItemId = parseInt(editedItemIdStr);
                    result.editedItemId = editedItemId;
                }

                let previousMainFiltersStr = sessionStorage.getItem(MainFiltersKey);
                if (previousMainFiltersStr) {
                    let previousMainFilters = JSON.parse(previousMainFiltersStr);
                    if (previousMainFilters.length > 0) {
                        result.mainFilter = previousMainFilters;
                        previousMainFilters.forEach(filter => {
                            result.previousMainFilters[filter.internalName] = filter.value;
                        });
                    }
                }

                this.clearSavedFilterData();
                this.clearSavedFilterData(true);

                return result;
            }
        }

        this.clearSavedFilterData();
        this.clearSavedFilterData(true);

        return undefined;
    }

    private clearSavedFilterData(isForInner: boolean = false) {
        sessionStorage.removeItem(this.getKeyName(GridFiltersKey, isForInner));
        sessionStorage.removeItem(this.getKeyName(PageNumberKey, isForInner));
        sessionStorage.removeItem(this.getKeyName(MainFiltersKey, isForInner));
        sessionStorage.removeItem(this.getKeyName(PreviousProjectTypeKey, isForInner));
        sessionStorage.removeItem(this.getKeyName(SortedColumnKey, isForInner));
        sessionStorage.removeItem(this.getKeyName(EditedItemIdKey, isForInner));
        sessionStorage.removeItem(this.getKeyName(PageSizeKey, isForInner));
    }

    private getKeyName(keyName: string, isForInner: boolean) {
        return isForInner ? InnerParamsKey + keyName : keyName;
    }


}